import { ScheduleJobDetail, ScheduleJobLifecycle } from "./ScheduleJobDetail";

/** One row on the schedule: a lot, represented by whichever of its steps the CM should look at next. */
export interface ScheduleLotCard {
  lotId: number;
  communityId: number;
  /** Lot block, one of the two identifiers the division can choose between. */
  lotBlockText: string;
  /** Sales community, which leads the secondary line. */
  communityName: string;
  /** Filter value for the Financial Community section. */
  financialCommunityName: string;
  /** The other identifier, and the sort key when the division leads with Address. */
  streetAddress: string;
  /** Every step landing on this card's day, in step order, rendered comma separated. */
  steps: ScheduleJobDetail[];
  /** Drives both the section the card lands in and the date shown on it. */
  effectiveDate: Date;
  isOverdue: boolean;
  /** The step the card is anchored to. */
  job: ScheduleJobDetail;
}

export interface ScheduleLotCardContext {
  /** True when the step is close enough to its scheduled date to be worked without starting early. */
  isWithinReadyWindow(job: ScheduleJobDetail): boolean;
  isOverdue(job: ScheduleJobDetail): boolean;
  /** True while the step still holds data on this device that has not finished uploading. */
  hasPendingUpload(job: ScheduleJobDetail): boolean;
  wasCompletedToday(job: ScheduleJobDetail): boolean;
}

function compareByDateThenStep(left: ScheduleJobDetail, right: ScheduleJobDetail): number {
  const dateDiff = left.getEffectiveFilterDate().getTime() - right.getEffectiveFilterDate().getTime();

  return dateDiff !== 0 ? dateDiff : left.buildQualityStep - right.buildQualityStep;
}

function toDayKey(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Finished work leaves the schedule at the end of the day it was completed, unless it is still uploading. */
function isStillShowing(job: ScheduleJobDetail, context: ScheduleLotCardContext): boolean {
  if (!job.isComplete) {
    return true;
  }

  return context.hasPendingUpload(job) || context.wasCompletedToday(job);
}

/**
 * Completed work takes the card in the two cases the CM still needs to see it: an upload that has
 * not finished, and a step finished today. Otherwise the lot is represented by its next active
 * step, so a finished step drops off the schedule at the end of the day it was completed.
 */
export function selectRepresentativeJob(
  jobsForLot: ScheduleJobDetail[],
  context: ScheduleLotCardContext
): ScheduleJobDetail | null {
  const eligible = jobsForLot.filter(job => job.isFeatureEnabled() && isStillShowing(job, context));
  const completed = eligible.filter(job => job.isComplete).sort(compareByDateThenStep);
  const mostRecentOf = (jobs: ScheduleJobDetail[]) => jobs[jobs.length - 1];

  const stillUploading = completed.filter(job => context.hasPendingUpload(job));

  if (stillUploading.length > 0) {
    return mostRecentOf(stillUploading);
  }

  const finishedToday = completed.filter(job => context.wasCompletedToday(job));

  if (finishedToday.length > 0) {
    return mostRecentOf(finishedToday);
  }

  const byLifecycle = new Map<ScheduleJobLifecycle, ScheduleJobDetail[]>([
    [ScheduleJobLifecycle.Active, []],
    [ScheduleJobLifecycle.Pending, []]
  ]);

  for (const job of eligible) {
    if (job.isComplete) {
      continue;
    }

    byLifecycle.get(job.getLifecycle(context.isWithinReadyWindow(job)))!.push(job);
  }

  for (const jobs of byLifecycle.values()) {
    jobs.sort(compareByDateThenStep);
  }

  return byLifecycle.get(ScheduleJobLifecycle.Active)![0]
    ?? byLifecycle.get(ScheduleJobLifecycle.Pending)![0]
    ?? null;
}

/** The anchor step plus any sibling landing on the same day, so the card can list "BQ8, BQ9". */
export function selectCardSteps(
  jobsForLot: ScheduleJobDetail[],
  context: ScheduleLotCardContext
): ScheduleJobDetail[] {
  const anchor = selectRepresentativeJob(jobsForLot, context);

  if (!anchor) {
    return [];
  }

  const anchorDay = toDayKey(anchor.getEffectiveFilterDate());
  const sameDay = jobsForLot
    .filter(job => job.isFeatureEnabled()
      && isStillShowing(job, context)
      && toDayKey(job.getEffectiveFilterDate()) === anchorDay)
    .sort((left, right) => left.buildQualityStep - right.buildQualityStep);

  const steps: ScheduleJobDetail[] = [];

  // Steps sharing a day are worked in order, so the next one is only revealed once the one
  // before it is both finished and uploaded.
  for (const job of sameDay) {
    steps.push(job);

    if (!job.isComplete || context.hasPendingUpload(job)) {
      break;
    }
  }

  // Never drop the anchor: it can be a later step whose upload is the reason the card is here.
  if (!steps.includes(anchor)) {
    steps.push(anchor);
    steps.sort((left, right) => left.buildQualityStep - right.buildQualityStep);
  }

  return steps;
}

export function buildScheduleLotCards(
  jobs: ScheduleJobDetail[],
  context: ScheduleLotCardContext
): ScheduleLotCard[] {
  const jobsByLotId = new Map<number, ScheduleJobDetail[]>();

  for (const job of jobs) {
    const jobsForLot = jobsByLotId.get(job.lotId) ?? [];
    jobsForLot.push(job);
    jobsByLotId.set(job.lotId, jobsForLot);
  }

  const cards: ScheduleLotCard[] = [];

  for (const [lotId, jobsForLot] of jobsByLotId) {
    const job = selectRepresentativeJob(jobsForLot, context);

    if (!job) {
      continue;
    }

    const steps = selectCardSteps(jobsForLot, context);

    cards.push({
      lotId,
      communityId: job.externalFinancialCommunityId,
      lotBlockText: `${job.lotBlock ?? ""}`,
      communityName: `${job.salesCommunityName ?? ""}`.trim(),
      financialCommunityName: `${job.financialCommunityName ?? job.salesCommunityName ?? ""}`.trim(),
      streetAddress: `${job.streetAddress ?? ""}`.trim(),
      steps,
      effectiveDate: job.getEffectiveFilterDate(),
      isOverdue: steps.some(step => context.isOverdue(step)),
      job
    });
  }

  return cards.sort((left, right) => {
    const dateDiff = left.effectiveDate.getTime() - right.effectiveDate.getTime();

    return dateDiff !== 0
      ? dateDiff
      : left.lotBlockText.localeCompare(right.lotBlockText, undefined, { numeric: true });
  });
}
